// Oike · Mi recorrido — the pure rules of the person's journey: what each
// indicator counts, the milestones of a process, small recognitions, the one
// next step, what an approved profile says and the recommended route.
// Shared by the server (shared/journey.ts, shared/personSpace.ts), the screens
// and the tests. No network, no AI, no scores.
//
// - An indicator always says what it counts ("3 de 8 sesiones realizadas") and
//   exists only when there is a defined total. Services are never mixed and
//   there is no global percentage.
// - A scheduled session or 1:1 is not done; a lesson with a task is done only
//   when the coach completed it after review (learningConfig.progressOf).
// - Recognitions come from real milestones only. No streaks, no rankings, and
//   finishing something is never presented as proof of a skill.
import { COURSE_TOPICS, COURSE_LEVELS, COURSE_ACCESS, APTITUDE_NAMES, MAX_RECOMMENDATIONS, RULE_LIMITS } from './journeyConfig.js';

const UNITS = {
  sessions: ['sesión realizada', 'sesiones realizadas'],
  lessons: ['lección completada', 'lecciones completadas'],
  activities: ['actividad hecha', 'actividades hechas'],
  goals: ['objetivo logrado', 'objetivos logrados'],
  stages: ['etapa completada', 'etapas completadas'],
  individualSessions: ['sesión individual realizada', 'sesiones individuales realizadas'],
  groupSessions: ['sesión grupal realizada', 'sesiones grupales realizadas'],
  sharedGoals: ['objetivo compartido logrado', 'objetivos compartidos logrados'],
};

/** What each indicator counts, in words (the same sentence for the person, the coach and whoever leads a team). */
export const METRIC_COUNTS = {
  sessions: 'Cuenta las sesiones con estado realizada. Una sesión agendada no cuenta.',
  individualSessions: 'Sesiones individuales propias realizadas. Agendadas no cuentan.',
  groupSessions: 'Sesiones grupales realizadas. Agendadas no cuentan.',
  activities: 'Actividades propias marcadas como hechas. Las grupales no cuentan acá.',
  goals: 'Objetivos del acuerdo individual que la coach marcó como logrados.',
  sharedGoals: 'Objetivos del acuerdo del equipo que la coach marcó como logrados.',
  lessons: 'Lecciones marcadas como vistas y trabajos completados después de la revisión. Abrir no cuenta.',
  stages: 'Cuestionarios enviados, entrevista realizada y resultados compartidos (solo las etapas que tiene).',
};

/** "3 de 8 sesiones realizadas", or null when there is no defined total (never an invented percentage). */
export function metric(done, total, unit) {
  const t = Number(total) || 0;
  if (t <= 0) return null;
  const d = Math.max(0, Math.min(Number(done) || 0, t));
  const [one, many] = UNITS[unit] || ['', ''];
  return { done: d, total: t, unit, label: `${d} de ${t} ${t === 1 ? one : many}`, finished: d === t };
}

// ── Diagnostic: forms, 1:1 and shared results, only the stages this participation has ──

const ONE_ON_ONE = { scheduled: 'Agendada', done: 'Realizada' };

/**
 * forms: [{ label, status }] (status submitted / draft / not_started);
 * sessions: [{ at, status }] without the cancelled ones; documents: the results shared in the space.
 * The 1:1 stage exists only when this participation has one; results always.
 */
export function diagnosticProgress({ forms = [], sessions = [], documents = [] }) {
  const stages = [];
  if (forms.length) {
    const sent = forms.filter(f => f.status === 'submitted').length;
    stages.push({ key: 'forms', title: forms.length === 1 ? 'Cuestionario' : 'Cuestionarios', done: sent === forms.length, detail: `${sent} de ${forms.length} ${forms.length === 1 ? 'enviado' : 'enviados'}` });
  }
  if (sessions.length) {
    const done = sessions.some(s => s.status === 'done');
    const scheduled = sessions.find(s => s.status === 'scheduled');
    stages.push({ key: 'interview', title: 'Entrevista 1:1', done, detail: done ? ONE_ON_ONE.done : scheduled ? ONE_ON_ONE.scheduled : 'Por agendar', at: (done ? sessions.find(s => s.status === 'done') : scheduled)?.at || null });
  }
  stages.push({ key: 'results', title: 'Resultados compartidos', done: documents.length > 0, detail: documents.length ? (documents.length === 1 ? '1 resultado disponible' : `${documents.length} resultados disponibles`) : 'En preparación' });
  const current = stages.findIndex(s => !s.done);
  return { stages: stages.map((s, i) => ({ ...s, state: s.done ? 'done' : i === current ? 'current' : 'ahead' })), metric: metric(stages.filter(s => s.done).length, stages.length, 'stages') };
}

// ── Coaching: sessions done, activities and shared goals ──

/**
 * sessions: [{ number, title, kind, status, startsAt }] of this participation (not cancelled);
 * in a team, the group sessions count for the progress (as coachingJourney.countedSessions).
 * activities: the person's own activities [{ status }]; goals: [{ text, status }] agreed with the coach.
 */
export function coachingProgress({ sessions = [], team = false, activities = [], goals = [] }) {
  const counted = sessions.filter(s => s.status !== 'cancelled' && (!team || s.kind === 'group'));
  const ordered = [...counted].sort((a, b) => (a.number || 99) - (b.number || 99) || String(a.startsAt || '').localeCompare(String(b.startsAt || '')));
  const next = ordered.findIndex(s => s.status !== 'done');
  const milestones = ordered.map((s, i) => ({
    key: `s${s.number || i + 1}`, title: s.title || `Sesión ${s.number || i + 1}`,
    state: s.status === 'done' ? 'done' : i === next ? 'current' : 'ahead',
    detail: s.status === 'done' ? 'Realizada' : s.status === 'scheduled' ? 'Agendada' : 'Sin agendar', at: s.startsAt || null,
  }));
  const own = activities.filter(a => a.status !== 'withdrawn');
  const agreed = goals.filter(g => g && g.text);
  // In a team the main count is the group's sessions; the person's own individual sessions are a separate indicator
  const mine = team ? sessions.filter(s => s.status !== 'cancelled' && s.kind === 'individual') : [];
  return {
    sessions: metric(counted.filter(s => s.status === 'done').length, counted.length, team ? 'groupSessions' : 'sessions'),
    individual: team ? metric(mine.filter(s => s.status === 'done').length, mine.length, 'individualSessions') : null,
    activities: metric(own.filter(a => a.status === 'done').length, own.length, 'activities'),
    goals: metric(agreed.filter(g => g.status === 'achieved').length, agreed.length, 'goals'),
    milestones,
  };
}

/** A course: lessons completed under the real rules (seen, or task completed after review). */
export function courseMetric(c) {
  return metric(c.done, c.total, 'lessons');
}

// ── Recognitions: only real milestones, never streaks or rankings ──

/** cards: the space cards with their progress; courses: the person's courses. */
export function recognitions(cards = [], courses = []) {
  const out = [];
  for (const c of cards) {
    const p = c.progress;
    if (!p) continue;
    if (c.kind === 'diagnostic') {
      for (const s of p.stages || []) {
        if (!s.done) continue;
        if (s.key === 'forms') out.push({ key: `${c.key}:forms`, text: 'Enviaste tus cuestionarios del diagnóstico', where: c.name });
        if (s.key === 'interview') out.push({ key: `${c.key}:interview`, text: 'Hiciste tu entrevista 1:1', where: c.name, at: s.at });
        if (s.key === 'results') out.push({ key: `${c.key}:results`, text: 'Tus resultados del diagnóstico ya están disponibles', where: c.name });
      }
    }
    if (c.kind === 'coaching' && p.sessions) {
      const { done, total } = p.sessions;
      if (done >= 1) out.push({ key: `${c.key}:first`, text: 'Hiciste tu primera sesión', where: c.name });
      if (total > 2 && done * 2 >= total && done < total) out.push({ key: `${c.key}:half`, text: `Ya hiciste la mitad de las sesiones (${done} de ${total})`, where: c.name });
      if (done === total) out.push({ key: `${c.key}:all`, text: 'Hiciste todas las sesiones del proceso', where: c.name });
    }
    if (c.kind === 'coaching' && p.goals?.done) out.push({ key: `${c.key}:goals`, text: p.goals.done === 1 ? 'Tu coach y tú dieron por logrado un objetivo' : `Tu coach y tú dieron por logrados ${p.goals.done} objetivos`, where: c.name });
  }
  for (const c of courses) {
    if (c.status === 'completed') out.push({ key: `course:${c.id}:done`, text: `Terminaste el curso «${c.courseName}»`, where: 'Cursos', at: c.completedAt || null });
  }
  return out;
}

// ── The one next step: the soonest dated one, otherwise the first ──

export function pickNext(steps = []) {
  const list = steps.filter(Boolean);
  const dated = list.filter(s => s.when).sort((a, b) => String(a.when).localeCompare(String(b.when)));
  return dated[0] || list[0] || null;
}

// ── What an approved profile says (Diagnostic P3, the version for the person) ──

const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const SECTION_KEYS = {
  strengths: /lo que haces muy bien|fortalezas?/,
  focus: /en que enfocarte|areas? para trabajar|areas? de enfoque/,
};

/**
 * The sections of an approved profile, by their headings: "Lo que haces muy
 * bien" (strengths), "En qué enfocarte" (areas to work on) and, inside it, the
 * concrete actions ("En las próximas 4 semanas: …") as next steps. Only text
 * Ale approved; a section that is not found stays empty (nothing is guessed).
 */
export function profileSections(markdown) {
  const lines = String(markdown || '').replace(/\r/g, '').split('\n');
  const out = { strengths: '', focus: '', nextSteps: [] };
  let current = null;
  const buf = { strengths: [], focus: [] };
  for (const line of lines) {
    const h = line.match(/^#{1,4}\s+(.*)$/);
    if (h) {
      const title = fold(h[1]).replace(/^\d+[.)]\s*/, '');
      current = Object.keys(SECTION_KEYS).find(k => SECTION_KEYS[k].test(title)) || null;
      continue;
    }
    if (current) buf[current].push(line);
  }
  const clean = (arr) => arr.join('\n').replace(/^.*BORRADOR.*$/gim, '').replace(/\n{3,}/g, '\n\n').trim();
  out.strengths = clean(buf.strengths);
  out.focus = clean(buf.focus);
  const re = /en las pr[oó]ximas?\s+\d+\s+semanas:?\**:?\s*([^\n]+)/gi;
  let m;
  while ((m = re.exec(out.focus))) {
    const step = m[1].replace(/\*\*/g, '').trim();
    if (step) out.nextSteps.push(step);
  }
  return out;
}

/**
 * The aptitudes an approved section names, in the order of the config. Never
 * a source of recommendations on its own: with `allowed` (the keys the code
 * computed for that profile) it narrows them; without it, it only SUGGESTS
 * focus areas for Ale to confirm. Only the emphasized titles count ("**Discovery: llegar al impacto.**", as the
 * profile template writes each focus area), so a word in passing never makes
 * an aptitude a focus. `allowed`: only within these keys (the ones the code
 * computed for that profile, when the output keeps them).
 */
export function aptitudesNamed(text, allowed = null) {
  const titles = [...String(text || '').matchAll(/\*\*([^*]+)\*\*/g)].map(m => m[1])
    .filter(t => !/pr[oó]ximas?\s+\d+\s+semanas/i.test(t));
  const t = ` ${fold(titles.join(' | ')).replace(/[^a-z0-9]+/g, ' ')} `;
  return Object.entries(APTITUDE_NAMES)
    .filter(([key]) => !allowed || allowed.includes(key))
    .filter(([, names]) => names.some(n => t.includes(` ${n} `)))
    .map(([key]) => key);
}

// ── Course catalog properties (validated on the server before saving) ──

export const CATALOG_DEFAULT = { topics: [], level: null, prerequisites: [], minutes: null, basicPath: false, basicOrder: null, autoRecommend: false, access: 'assigned' };
const RECORD_RE = /^rec[A-Za-z0-9]{14}$/;

/** The catalog of a course from its fields (missing fields: the defaults). */
export function catalogOfFields(fields = {}, parse = (v, d) => { try { return v ? JSON.parse(v) : d; } catch { return d; } }) {
  const sel = (v) => (v && typeof v === 'object' ? v.name : v) || null;
  const topics = parse(fields['Topics'], []);
  const prerequisites = parse(fields['Prerequisites'], []);
  const level = sel(fields['Level']);
  const access = sel(fields['Access']);
  return {
    topics: Array.isArray(topics) ? topics.filter(k => COURSE_TOPICS.some(x => x.key === k)) : [],
    level: COURSE_LEVELS.some(x => x.key === level) ? level : null,
    prerequisites: Array.isArray(prerequisites) ? prerequisites.filter(x => RECORD_RE.test(String(x))) : [],
    minutes: Number.isFinite(Number(fields['Estimated Minutes'])) && Number(fields['Estimated Minutes']) > 0 ? Math.round(Number(fields['Estimated Minutes'])) : null,
    basicPath: fields['Basic Path'] === true,
    basicOrder: Number.isFinite(Number(fields['Basic Path Order'])) && fields['Basic Path Order'] !== null && fields['Basic Path Order'] !== undefined && fields['Basic Path Order'] !== '' ? Number(fields['Basic Path Order']) : null,
    autoRecommend: fields['Auto Recommend'] === true,
    access: COURSE_ACCESS.some(x => x.key === access) ? access : 'assigned',
  };
}

/** Input from the course editor → valid catalog, or throws Error(code). */
export function catalogInput(v, courseId, courseIds) {
  if (!v || typeof v !== 'object') throw new Error('invalid catalog');
  const topics = [...new Set((Array.isArray(v.topics) ? v.topics : []))];
  if (topics.some(k => !COURSE_TOPICS.some(x => x.key === k))) throw new Error('invalid topic');
  if (topics.length > 6) throw new Error('too many topics');
  const level = v.level === null || v.level === '' || v.level === undefined ? null : v.level;
  if (level !== null && !COURSE_LEVELS.some(x => x.key === level)) throw new Error('invalid level');
  const prerequisites = [...new Set(Array.isArray(v.prerequisites) ? v.prerequisites : [])];
  if (prerequisites.length > 5) throw new Error('too many prerequisites');
  for (const id of prerequisites) if (!RECORD_RE.test(String(id)) || id === courseId || !courseIds.has(id)) throw new Error('invalid prerequisite');
  const minutes = v.minutes === null || v.minutes === '' || v.minutes === undefined ? null : Math.round(Number(v.minutes));
  if (minutes !== null && (!Number.isFinite(minutes) || minutes < 1 || minutes > 6000)) throw new Error('invalid minutes');
  const basicOrder = v.basicOrder === null || v.basicOrder === '' || v.basicOrder === undefined ? null : Math.round(Number(v.basicOrder));
  if (basicOrder !== null && (!Number.isFinite(basicOrder) || basicOrder < 0 || basicOrder > 999)) throw new Error('invalid order');
  const access = v.access || 'assigned';
  if (!COURSE_ACCESS.some(x => x.key === access)) throw new Error('invalid access');
  if (v.basicPath === true && level === 'advanced') throw new Error('advanced in basic path');
  return { topics, level, prerequisites, minutes, basicPath: v.basicPath === true, basicOrder, autoRecommend: v.autoRecommend === true, access };
}

/** The Airtable fields of a catalog. */
export const catalogFields = (c) => ({
  'Topics': JSON.stringify(c.topics), 'Level': c.level, 'Prerequisites': JSON.stringify(c.prerequisites), 'Estimated Minutes': c.minutes,
  'Basic Path': c.basicPath, 'Basic Path Order': c.basicOrder, 'Auto Recommend': c.autoRecommend, 'Access': c.access,
});

/** "45 min", "1 h 30 min". */
export function durationLabel(minutes) {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
}

// ── The recommended route ──

/**
 * results: { status, reference?: { participantId, outputId, diagnosticName, focus: [keys], strengths: [keys], names: {key: label} } }
 *   status 'ready' only when an approved profile of this person is shared in their space and chosen as reference.
 * courses: [{ id, name, status, version, catalog }] (the coach's courses, any status)
 * enrollments: [{ id, courseId, status, done, total, origin }] (withdrawn included: never recommended again)
 * dismissed: [{ courseId }] (Ale discarded it for this person)
 * rules: the active rules in force (Coaching Settings › journey_rules); rulesVersion: their version
 * audience: 'person' leaves out what the person cannot start (a course only the coach assigns), so a
 *   person without a valid personal match gets the basic route; 'coach' shows every match.
 *
 * Returns { mode, items, skipped } where mode is 'personalized', 'basic' or 'empty'.
 * items: one per course, never repeated: { courseId, name, level, minutes, state, reason, origin, offer }
 *   state: recommended / assigned / in_progress / completed; offer: the person may start it (open access, not enrolled).
 * skipped: what a rule or the basic route matched and why it is not offered (for Ale in Personas).
 * Nothing here writes: recalculating never changes or removes an assignment.
 */
/** A course specific to sales: marked so by Ale with the topic "sales" (never inferred from other topics). */
export const isSalesCourse = (c) => (c?.catalog?.topics || []).includes('sales');

export function recommendRoute({ results = { status: 'none' }, courses = [], enrollments = [], dismissed = [], rules = [], rulesVersion = 0, audience = 'coach', salesAllowed = false }) {
  const byId = new Map(courses.map(c => [c.id, c]));
  const enrolled = new Map();
  for (const e of enrollments) {
    const prev = enrolled.get(e.courseId);
    // The active or completed one decides over a withdrawn one
    if (!prev || (prev.status === 'withdrawn' && e.status !== 'withdrawn')) enrolled.set(e.courseId, e);
  }
  const completed = new Set(enrollments.filter(e => e.status === 'completed').map(e => e.courseId));
  const dismissedSet = new Set(dismissed.map(d => d.courseId));
  const published = (c) => c && c.status === 'active' && (c.version || 0) > 0;
  const skipped = [];
  const prereqsMet = (c) => (c.catalog.prerequisites || []).every(id => completed.has(id));
  const stateOf = (e) => (!e ? 'recommended' : e.status === 'completed' ? 'completed' : (e.done || 0) > 0 ? 'in_progress' : 'assigned');

  /** Whether a course can be a new suggestion; the reason it cannot otherwise. */
  const blocker = (c) => {
    if (!published(c)) return 'not_published';
    const e = enrolled.get(c.id);
    if (e?.status === 'withdrawn') return 'withdrawn';
    if (dismissedSet.has(c.id) && !e) return 'dismissed';
    if (!prereqsMet(c)) return 'prerequisites';
    if (audience === 'person' && !e && c.catalog.access !== 'open') return 'assignment_only';
    // Sales courses are never offered on their own to someone who did not choose sales (an assigned one stays)
    if (!e && !salesAllowed && isSalesCourse(c)) return 'sales_not_chosen';
    return null;
  };

  const items = [];
  const seen = new Set();
  const push = (c, extra) => {
    if (seen.has(c.id)) {
      const it = items.find(x => x.courseId === c.id);
      if (it && extra.aptitude && !it.aptitudes.includes(extra.aptitude)) it.aptitudes.push(extra.aptitude);
      return;
    }
    seen.add(c.id);
    const e = enrolled.get(c.id) && enrolled.get(c.id).status !== 'withdrawn' ? enrolled.get(c.id) : null;
    items.push({
      courseId: c.id, name: c.name, level: c.catalog.level, minutes: c.catalog.minutes, topics: c.catalog.topics,
      state: stateOf(e), enrollmentId: e?.id || null, access: c.catalog.access, offer: !e && c.catalog.access === 'open',
      aptitudes: extra.aptitude ? [extra.aptitude] : [], ...extra,
    });
  };

  // Personalized: only with a ready reference result
  if (results.status === 'ready' && results.reference) {
    const ref = results.reference;
    for (const rule of rules.filter(r => r && r.active === true)) {
      const named = rule.source === 'strength' ? ref.strengths : ref.focus;
      if (!(named || []).includes(rule.aptitude)) continue;
      const levels = Array.isArray(rule.courseLevels) && rule.courseLevels.length ? rule.courseLevels : ['intro', 'intermediate'];
      const matches = courses.filter(c => c.catalog?.autoRecommend && (c.catalog.topics || []).some(t => (rule.topics || []).includes(t)))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      for (const c of matches) {
        if (!c.catalog.level || !levels.includes(c.catalog.level)) { skipped.push({ courseId: c.id, name: c.name, ruleId: rule.id, why: 'level' }); continue; }
        const why = blocker(c);
        if (why) { skipped.push({ courseId: c.id, name: c.name, ruleId: rule.id, why }); continue; }
        const aptitude = (ref.names || {})[rule.aptitude] || rule.aptitude;
        push(c, {
          kind: 'recommendation', ruleId: rule.id, rulesVersion, outputId: ref.outputId, participantId: ref.participantId, aptitude: rule.aptitude,
          reason: String(rule.reason || '').replace('{aptitude}', aptitude),
        });
      }
    }
    // A manageable route: the courses already started stay, at most MAX new suggestions
    let fresh = 0;
    for (let i = 0; i < items.length; i++) {
      if (items[i].state !== 'recommended') continue;
      fresh++;
      if (fresh > MAX_RECOMMENDATIONS) { skipped.push({ courseId: items[i].courseId, name: items[i].name, ruleId: items[i].ruleId, why: 'limit' }); items.splice(i, 1); i--; }
    }
  }

  const personalized = items.length > 0;
  if (!personalized) {
    // The basic route: published courses marked for it, in their order (never advanced)
    const basic = courses.filter(c => c.catalog?.basicPath && c.catalog.level !== 'advanced')
      .sort((a, b) => (a.catalog.basicOrder ?? 999) - (b.catalog.basicOrder ?? 999) || String(a.name).localeCompare(String(b.name)));
    for (const c of basic) {
      const why = blocker(c);
      if (why) { skipped.push({ courseId: c.id, name: c.name, ruleId: null, why }); continue; }
      push(c, { kind: 'basic', ruleId: null, rulesVersion, outputId: null, participantId: null, aptitude: null, reason: 'Es parte de la ruta básica de Oike.' });
    }
  }

  // Courses of this route the person already has keep their state; courses started from an
  // earlier recommendation that no longer applies stay as assigned, without its reason
  for (const e of enrollments) {
    if (e.status === 'withdrawn' || seen.has(e.courseId)) continue;
    if (!['recommendation', 'basic', 'self'].includes(e.origin?.kind)) continue;
    const c = byId.get(e.courseId);
    if (!c) continue;
    push(c, { kind: e.origin.kind, ruleId: null, rulesVersion: null, outputId: null, participantId: null, aptitude: null, reason: null, stale: true });
  }

  // Read as a path: what is done, what is in course, what is assigned, then the suggestions (each group keeps its order)
  const rank = { completed: 0, in_progress: 1, assigned: 2, recommended: 3 };
  const ordered = items.map((it, i) => ({ it, i })).sort((a, b) => rank[a.it.state] - rank[b.it.state] || a.i - b.i).map(x => x.it);
  return { mode: personalized ? 'personalized' : ordered.length ? 'basic' : 'empty', items: ordered, skipped };
}

/** What the person receives of a route computed with audience 'person': no skipped list, no access keys. */
export function personRoute(route) {
  const items = route.items.filter(i => i.state !== 'recommended' || i.offer).map(({ access: _a, ...i }) => i);
  return { mode: items.length ? route.mode : 'empty', items };
}

// ── The rules Ale manages in Oike (validated on the server before saving) ──

const RULE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9-]{2,40}$/;

/** The list from the rules editor → valid rules, or throws Error(code). Ids stay; a new rule gets one. */
export function rulesInput(list, aptitudeKeys, newId = () => `r-${Math.random().toString(36).slice(2, 8)}`) {
  if (!Array.isArray(list)) throw new Error('invalid rules');
  if (list.length > RULE_LIMITS.rules) throw new Error('too many rules');
  const seen = new Set();
  return list.map(r => {
    if (!r || typeof r !== 'object') throw new Error('invalid rule');
    const id = typeof r.id === 'string' && RULE_ID_RE.test(r.id) && !seen.has(r.id) ? r.id : newId();
    seen.add(id);
    const source = r.source === 'strength' ? 'strength' : r.source === 'focus' || r.source === undefined ? 'focus' : null;
    if (!source) throw new Error('invalid source');
    if (!aptitudeKeys.includes(r.aptitude)) throw new Error('invalid aptitude');
    const topics = [...new Set(Array.isArray(r.topics) ? r.topics : [])];
    if (!topics.length) throw new Error('missing topics');
    if (topics.length > RULE_LIMITS.topics || topics.some(k => !COURSE_TOPICS.some(x => x.key === k))) throw new Error('invalid topic');
    const courseLevels = [...new Set(Array.isArray(r.courseLevels) ? r.courseLevels : [])];
    if (!courseLevels.length || courseLevels.some(k => !COURSE_LEVELS.some(x => x.key === k))) throw new Error('invalid levels');
    const reason = String(r.reason ?? '').trim();
    if (!reason) throw new Error('missing reason');
    if (reason.length > RULE_LIMITS.reason) throw new Error('reason too long');
    const note = String(r.note ?? '').trim();
    if (note.length > RULE_LIMITS.note) throw new Error('note too long');
    return { id, active: r.active === true, source, aptitude: r.aptitude, topics, courseLevels, reason, ...(note ? { note } : {}) };
  });
}

/** What changed between two versions of the rules, per rule id (for the history). */
export function rulesDiff(before = [], after = []) {
  const prev = new Map(before.map(r => [r.id, r]));
  const out = [];
  for (const r of after) {
    const p = prev.get(r.id);
    if (!p) { out.push({ id: r.id, change: 'added' }); continue; }
    const { active: a1, ...rest1 } = p; const { active: a2, ...rest2 } = r;
    if (JSON.stringify(rest1) !== JSON.stringify(rest2)) out.push({ id: r.id, change: 'edited' });
    if (!!a1 !== !!a2) out.push({ id: r.id, change: a2 ? 'activated' : 'deactivated' });
  }
  for (const p of before) if (!after.some(r => r.id === p.id)) out.push({ id: p.id, change: 'removed' });
  return out;
}

export const ROUTE_STATES = { recommended: 'Recomendado para ti', assigned: 'Asignado', in_progress: 'En curso', completed: 'Completado' };
export const SKIP_REASONS = {
  not_published: 'No está publicado',
  withdrawn: 'Se le retiró antes',
  dismissed: 'Lo descartaste',
  prerequisites: 'Le faltan requisitos previos',
  level: 'El nivel no coincide con la regla',
  limit: 'Ya hay suficientes sugerencias',
  assignment_only: 'Solo por asignación: no puede empezarlo por su cuenta',
  sales_not_chosen: 'Curso de ventas: no eligió la especialidad ventas',
};
